/**
 * useMoveToOrder — the Move to Order # rule from RunCtrl, shared by the customer detail
 * and the Run map's sheet, so both move a stop and save it the same way.
 */

import { useCallback, useMemo } from 'react';

import { useRunsheetContext } from '@/contexts/RunsheetContext';
import { reorderLines } from '@/hooks/useRunsheet';
import type { RunsheetLine } from '@/types';

interface UseMoveToOrderReturn {
  /** The run orders a stop can move to, for the Move to Order # Select. */
  orderOptions: string[];
  /**
   * Moves `line` to the place of the stop numbered `displayorder` and renumbers the run,
   * saved through `moveLine`. The selected stop takes its new number too. Returns the
   * ids of the stops whose number changed.
   */
  moveToOrder: (line: RunsheetLine, displayorder: number) => number[];
}

export function useMoveToOrder(): UseMoveToOrderReturn {
  const { runsheet, moveLine, fetchRunsheet, setCurrent } = useRunsheetContext();

  const orderOptions = useMemo(
    () => [
      ...new Set(
        (runsheet?.lines ?? []).filter(l => l.displayorder > 0).map(l => String(l.displayorder)),
      ),
    ],
    [runsheet],
  );

  const moveToOrder = useCallback(
    (line: RunsheetLine, displayorder: number): number[] => {
      if (!runsheet) return [];
      const fromIndex = runsheet.lines.findIndex(l => l.id === line.id);
      const toIndex = runsheet.lines.findIndex(l => l.displayorder === displayorder);
      if (fromIndex === -1 || toIndex === -1) return [];

      const before = new Map(runsheet.lines.map(l => [l.id, l.displayorder]));
      const after = reorderLines(runsheet.lines, runsheet.runid, line, fromIndex, toIndex);
      void moveLine(line, fromIndex, toIndex).then(() => void fetchRunsheet());
      // The selected stop is a working copy, so only its number follows the move.
      const orders = new Map(after.map(l => [l.id, l.displayorder]));
      setCurrent(prev =>
        prev ? { ...prev, displayorder: orders.get(prev.id) ?? prev.displayorder } : prev,
      );
      return after.filter(l => before.get(l.id) !== l.displayorder).map(l => l.id);
    },
    [runsheet, moveLine, fetchRunsheet, setCurrent],
  );

  return { orderOptions, moveToOrder };
}
